import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { submitEnquiry } from "../lib/api";
import { GRADES, PROGRAM_OPTIONS } from "../data/site";

const initial = { name: "", phone: "", grade: "", program: "", message: "" };

export default function EnquiryForm({ defaultProgram }) {
  const [form, setForm] = useState({ ...initial, program: defaultProgram || "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.grade || !form.program) {
      toast.error("Please fill in name, phone, grade and program.");
      return;
    }
    if (!/^[0-9+\-\s]{7,15}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(form);
      toast.success("Thank you! We'll get back to you within 48 hours.");
      setDone(true);
      setForm({ ...initial });
    } catch (err) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-3xl bg-teal-brand text-white p-10 text-center"
        data-testid="enquiry-success"
      >
        <p className="font-display text-3xl font-bold mb-2">You&apos;re in!</p>
        <p className="text-white/90">
          Our team will reach out within 48 hours to book your free consultation.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-6 underline font-semibold"
          data-testid="enquiry-reset"
        >
          Submit another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="font-semibold text-navy">Student / Parent Name</Label>
          <Input
            id="name"
            data-testid="input-name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Aarav Shah"
            className="mt-1.5 h-12 rounded-xl bg-cream border-navy/15 focus-visible:ring-marigold"
          />
        </div>
        <div>
          <Label htmlFor="phone" className="font-semibold text-navy">Phone Number</Label>
          <Input
            id="phone"
            data-testid="input-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+91 ..."
            className="mt-1.5 h-12 rounded-xl bg-cream border-navy/15 focus-visible:ring-marigold"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="font-semibold text-navy">Grade</Label>
          <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
            <SelectTrigger
              data-testid="select-grade"
              className="mt-1.5 h-12 rounded-xl bg-cream border-navy/15 focus:ring-marigold"
            >
              <SelectValue placeholder="Select grade" />
            </SelectTrigger>
            <SelectContent>
              {GRADES.map((g) => (
                <SelectItem key={g} value={g}>{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="font-semibold text-navy">Program</Label>
          <Select value={form.program} onValueChange={(v) => set("program", v)}>
            <SelectTrigger
              data-testid="select-program"
              className="mt-1.5 h-12 rounded-xl bg-cream border-navy/15 focus:ring-marigold"
            >
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {PROGRAM_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="message" className="font-semibold text-navy">Anything we should know? (optional)</Label>
        <Textarea
          id="message"
          data-testid="input-message"
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder="Your child's goals, preferred timings, etc."
          className="mt-1.5 rounded-xl bg-cream border-navy/15 focus-visible:ring-marigold min-h-[110px]"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="enquiry-submit"
        className="w-full h-14 rounded-full bg-marigold text-white font-bold text-lg flex items-center justify-center gap-2 shadow-[0_6px_0_0_#c85c14] hover:translate-y-0.5 hover:shadow-[0_3px_0_0_#c85c14] transition-all disabled:opacity-70"
      >
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
        {loading ? "Sending..." : "Book My Free Consultation"}
      </button>
    </form>
  );
}
